import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import './TaxDetailPage.css'; // Ensure this file contains the necessary styles
import PropertyInfo from './PropertyInfo';

const TaxDetailPage = () => {
  const { customerId, taxid, taxyear } = useParams();
  const { dataClient, dataProperty } = useOutletContext();
  const [dataTax, setDataTax] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTaxData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_PROXY_URL}/api/taxDetail/${customerId}/${taxid}/${taxyear}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        const list = result?.data?.result ?? [];
        setDataTax(Array.isArray(list) ? list : []);

      } catch (error) {
        console.error('Error fetching tax data:', error);
        setError(error);
      }
      finally
      {
        setIsLoading(false);
      }
    };

    fetchTaxData();
  }, [customerId, taxid, taxyear]);

  if (error) {
    return <div className="error">Error: {error.message}</div>;
  }

if (isLoading) {
  return <div className="loading">Loading...</div>;
}

if (dataTax.length === 0) {
  return (
    <div className="container">
      <div className="content">
        {dataProperty ? (
          <PropertyInfo dataProperty={dataProperty} />
        ) : (
          <div className="loading">Loading Property Info...</div>
        )}
      </div>

      <div className="table-container">
        <div className="no-records">No records found</div>
      </div>
    </div>
  );
}


  // Helper function to format dates
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const options = { month: '2-digit', day: '2-digit', year: '2-digit' };
    return date.toLocaleDateString(undefined, options);
  };

  // Calculate totals
  const totals = dataTax.reduce(
    (acc, item) => {
      acc.amount += item.amount;
      acc.penalty += item.penalty;
      acc.interest += item.interest;
      return acc;
    },
    { amount: 0, penalty: 0, interest: 0 }
  );

  const formattedTotals = {
    amount:   totals.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    penalty:  totals.penalty.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    interest: totals.interest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  };

  // Determine row styles grouped by Tax Date
  let lastDate = null;
  let isOddRow = true;

  return (
    <div className="container">
      <div className="content">
        {dataProperty ? (
          <PropertyInfo dataProperty={dataProperty} />
        ) : (
          <div className="loading">Loading Property Info...</div>
        )}
      </div>
      <div className="table-container">
        <table className="responsive-table">
          <thead>
            <tr>
              <th>District</th>
              <th>Tax Date</th>
              <th>Int Date</th>
              <th>PD?</th>
              <th>Tax Amt</th>
              <th>Penalty</th>
              <th>Interest</th>
            </tr>
          </thead>
          <tbody>
            {dataTax.map((item, index) => {
              // Check if the Tax Date has changed
              if (formatDate(item.date) !== lastDate) {
                lastDate = formatDate(item.date);
                isOddRow = !isOddRow; // Toggle row style
              }

              return (
                <tr
                  key={index}
                  className={isOddRow ? 'odd-row' : 'even-row'} // Apply appropriate class
                >
                  <td>
                    {item.district.trim()} {item.description.trim()}
                  </td>
                  <td>{formatDate(item.date)}</td>
                  <td>{formatDate(item.date)}</td>
                  <td>{item.isPaid}</td>
                  <td>{item.amount.toFixed(2)}</td>
                  <td>{item.penalty.toFixed(2)}</td>
                  <td>{item.interest.toFixed(2)}</td>
                </tr>
              );
            })}
            {/* Totals Row */}
            <tr className="totals-row">
              <td colSpan="4" className="totals-label">
                Totals for {taxyear}
              </td>
              <td>{formattedTotals.amount}</td>
              <td>{formattedTotals.penalty}</td>
              <td>{formattedTotals.interest}</td>
            </tr>
            {/* Grand Total Row */}
            <tr className="grand-total-row">
              <td colSpan="4" className="grand-total-label">
                Total Tax, Penalty and Interest
              </td>
              <td colSpan="4" className="right-align">{formattedTotals.amount}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TaxDetailPage;
